import React from 'react';
import i18n from 'i18next';
import moment from 'moment';
import store from 'redux/store';
import { confirmAlert } from 'react-confirm-alert';
import { Provider } from 'react-redux';
import ConfirmModal from 'shared/components/modals/ConfirmModal';

export const getError = err => {
  try {
    if (err && err.errors) {
      return err.errors[0].message;
    }
    if (err && err.response && err.response.data) {
      return err.response.data.message;
    }
    if (err && err.response && err.response.statusText) {
      return err && err.response && err.response.statusText;
    }
    if (err && err.data && err.data.message) {
      return err.data.message;
    }
    if (err && err.message) {
      return err.message;
    }
    return err;
  } catch (e) {
    return 'Something went wrong';
  }
};

export function createReducer(initialState, handlers) {
  return function reducer(state = initialState, action) {
    if (handlers.hasOwnProperty(action.type)) {
      return handlers[action.type](state, action);
    }
    return state;
  };
}

export const getENV = () => {
  const env = process.env.REACT_APP_API_URL_ENV || process.env.NODE_ENV;
  return env === 'development' ? 'stage' : env || 'stage';
};

export const getMonths = () => {
  return new Array(12).fill('').map((v, i) => ({
    name: moment()
      .month(i)
      .locale(i18n.language)
      .format('MMMM'),
    value: i + 1,
  }));
};

export const getDays = month => {
  if (!month) return [];
  const daysCount = new Date(new Date().getFullYear(), month, 0).getDate();
  return new Array(daysCount).fill('').map((v, i) => ({ name: i + 1, value: i + 1 }));
};

export const getYears = () => {
  const current = new Date().getFullYear();
  const min = current - 10 - 100;
  const arr = new Array(100).fill('').map((v, i) => ({ name: min + i + 1, value: min + i + 1 }));
  return arr.reverse();
};

export const confirmBox = options => {
  confirmAlert({
    customUI: ({ onClose }) => (
      <Provider store={store}>
        <ConfirmModal onClose={onClose} {...options} />
      </Provider>
    ),
  });
};

export const showPrice = (product, key = 'price', displayMode = 'compact') => {
  if (!product) return i18n.t('price_not_available');

  // Check for original price first
  const price = product[key] ? `${product.currency || '$'} ${product[key].toFixed(2)}` : null;

  // If no original price, check for price range
  if (!price && product.priceRange) {
    const { min, max, currency = '$', count } = product.priceRange;

    if (!min || min <= 0) return i18n.t('price_not_available');

    const hasMultipleDeals = count > 1;
    const minPrice = `${currency}${min.toFixed(2)}`;
    const maxPrice = max && max > 0 ? `${currency}${max.toFixed(2)}` : null;

    switch (displayMode) {
      case 'range': // Show full range: $12.99 - $45.99
        return hasMultipleDeals && maxPrice && max > min ? `${minPrice} - ${maxPrice}` : minPrice;

      case 'from': // Show "from" format: from $12.99
        return hasMultipleDeals ? `from ${minPrice}` : minPrice;

      case 'compact': // Default compact format for list view
      default:
        return hasMultipleDeals ? `from ${minPrice}` : minPrice;
    }
  }

  return price || i18n.t('price_not_available');
};

export const arrayToObj = (array, key = 'id') => {
  const result = {};
  array.forEach(item => {
    result[item[key]] = item;
  });
  return result;
};

export const isEmail = email => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
};

export const openEmailClient = (email, subject, body) => {
  window.location.href = `mailto:${email}?subject=${subject}&body=${encodeURIComponent(body)}`;
};

export const getUserLocale = () => {
  return navigator.language ? navigator.language.split('-')?.[0] : 'en';
};

export const preloadImages = images => {
  images.forEach(src => {
    new Image().src = src;
  });
};

export const copyObject = obj => {
  if (!obj) return {};
  return JSON.parse(JSON.stringify(obj));
};
